import {
  Box,
  Fade,
  Grow,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState, useMemo, useRef } from "react";
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { X } from "lucide-react";
import styled from 'styled-components';

import NewToggleChat from "./NewToggleChat";
import { ChatBody } from "./New Chatbody";
import ConnectionStatus from "./ConnectionStatus";
import { useWebSocketContext } from "../context/Theme";
import CustomScrollbar from './CustomScrollbar';
import SearchPanel from "./SearchPanel";
import PushNotificationModal from './PushNotificationModal';
import pushNotificationManager from '../utils/pushNotifications';
import { createTranslator, getBrowserLanguage } from "../utils/i18n";

/**
 * Splits out any messages that appear to be "car-search" related.
 * Only do this when NOT on mobile. Otherwise keep them all in chat.
 *
 * If a message has both text + carouselItems, we split it into:
 *   1) A text-only message for the main chat
 *   2) A carousel-only message for the SearchPanel
 */
function partitionMessages(messages, isMobile) {
  if (isMobile) {
    // No partition for mobile. All messages go to chat.
    return { 
      chatMessages: messages, 
      searchResults: [] 
    };
  }

  const chatMessages = [];
  const searchResults = [];

  messages.forEach((msg, idx) => {
    const isCarSearchTool = Array.isArray(msg.tool)
      ? msg.tool.some((t) => typeof t === 'string' && t.includes("car_stock_search"))
      : typeof msg.tool === "string" && msg.tool.includes("car_stock_search");

    const hasCarousel = msg.carouselItems && msg.carouselItems.length > 0;

    // Only consider it a search result if it has actual carousel items
    if (hasCarousel) {
      if (msg.text) {
        // (A) Chat receives a text-only copy
        chatMessages.push({ 
          ...msg, 
          carouselItems: null,
          originalIndex: idx
        });
      }
      
      // (B) Search results get a carousel-only copy
      searchResults.push({ 
        ...msg, 
        text: null,
        originalIndex: idx
      });
    } else {
      // All other messages go to chat
      chatMessages.push(msg);
    }
  });

  return { chatMessages, searchResults };
}

// Update the ChatAndSearchContainer with improved layout
const MiaChatAndSearchContainer = styled(motion.div)`
  display: flex;
  position: fixed;
  bottom: ${props => props.$isMobile ? 0 : '100px'};
  right: ${props => props.$isMobile ? 0 : '2vw'};
  height: ${props => {
    if (props.$isMobile) {
      // Keep mobile height unchanged
      return props.$openedByTrigger || props.$isCompactMode ? '50%' : '100%';
    }
    // Consistent desktop height for both chat and search panel
    return 'calc(100vh - 120px)';
  }};
  top: ${props => {
    if (props.$isMobile) {
      return props.$openedByTrigger || props.$isCompactMode ? 'auto' : 0;
    }
    return 'auto';
  }};
  width: ${props => props.$isMobile ? '100vw' : 'auto'};
  perspective: 2000px;
  z-index: 1000;
  justify-content: ${props => props.$isMobile ? 'center' : 'flex-start'};
  flex-direction: row-reverse;
  transition: opacity 0.3s ease;
  opacity: 1;
  pointer-events: auto;
  gap: 20px; // Professional spacing between chat and search panel
  
  /* Ensure mobile car search displays correctly on top of everything */
  .car-search-overlay {
    z-index: 2000;
  }
  
  .car-search-container {
    z-index: 2001;
  }
  
  /* Fixed buttons at bottom of screen */
  .car-search-actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 2002;
    width: 100%;
    background: white;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    padding: 16px;
    display: flex;
    justify-content: space-between;
  }

  /* Styles for when page is scrolling underneath reduced chat */
  &.page-scrolling {
    opacity: 0.4;
    
    /* Keep the footer fully visible and opaque - Enhanced styling */
    .chat-footer.reduced-mode {
      opacity: 1 !important;
      visibility: visible !important;
      pointer-events: auto !important;
      background-color: #ffffff !important;
      box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.25) !important;
      border-top: 2px solid #2e7d32 !important;
      z-index: 2000 !important;
      position: fixed !important;
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
    }
    
    /* Enhanced CTA styling in transparent mode */
    .message-input-container.cta-active {
      background-color: rgba(76, 175, 80, 0.25) !important;
      border-color: #2e7d32 !important;
      box-shadow: 0 4px 12px rgba(46, 125, 50, 0.4) !important;
    }
    
    /* Personal data form should remain fully visible even in transparent mode */
    .personal-data-form,
    .personal-data-form-container {
      opacity: 1 !important;
      background-color: white !important;
      box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.2) !important;
      z-index: 2005 !important;
      visibility: visible !important;
      pointer-events: auto !important;
    }
  }
  
  /* Special handling for personal data form in reduced mode */
  &.isCompactMode,
  &.openedByTrigger {
    .personal-data-form,
    .personal-data-form-container {
      position: fixed !important;
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
      z-index: 2005 !important;
      max-height: 70vh !important;
      overflow-y: auto !important;
      background-color: white !important;
      border-radius: 12px 12px 0 0 !important;
      box-shadow: 0 -5px 15px rgba(0, 0, 0, 0.1) !important;
    }
  }
`;

// Update the ChatContainer with enhanced styling
const ChatContainer = styled(motion.div)`
  width: ${props => props.$isMobile ? '100%' : 'min(500px, 90vw)'};
  height: 100%;
  position: relative;
  z-index: 2;
  flex-shrink: 0;

  ${props => props.$isMobile && `
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    margin-top: 0;
  `}

  // Enhanced background with better visual hierarchy
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  
  /* Add styles to handle car search overlay visibility */
  &.car-search-active {
    z-index: 1;
  }
`;

// Update SearchPanelContainer with better positioning
const SearchPanelContainer = styled(motion.div)`
  position: relative;
  height: 100%; // Match parent container height exactly
  transform-origin: right center;
  z-index: 1;
  display: ${props => props.$isMobile ? 'none' : 'block'};
  flex-shrink: 0;
  // Remove margin-left as we now use gap in parent container
`;

// Update SemicircleButton with enhanced design
const SemicircleButton = styled(motion.button)`
  position: fixed;
  right: calc(min(500px, 90vw) - 1px);
  top: calc(50vh - 25px); // Center vertically
  width: 48px;
  height: 56px;
  background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
  color: white;
  border: none;
  border-radius: 28px 0 0 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: -4px 0 16px rgba(33, 150, 243, 0.3);
  z-index: 999;
  transition: all 0.3s ease;
  padding-right: 8px;
  overflow: visible;
  
  // Enhanced visual connection line
  &::before {
    content: '';
    position: absolute;
    left: -2px;
    top: 20%;
    bottom: 20%;
    width: 2px;
    background: linear-gradient(to bottom, transparent, #2196f3, transparent);
    opacity: 0.6;
  }

  &:hover {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    width: 56px;
    box-shadow: -6px 0 20px rgba(33, 150, 243, 0.4);
    transform: translateX(-4px);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Update the ResultBadge with better design
const ResultBadge = styled(motion.div)`
  position: absolute;
  top: -10px;
  left: -10px;
  background: linear-gradient(135deg, #ff5722 0%, #e64a19 100%);
  color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(255, 87, 34, 0.4);
  border: 2px solid white;
  
  // Subtle pulse animation
  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: inherit;
    opacity: 0.3;
    animation: pulse 2s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.3; }
    50% { transform: scale(1.2); opacity: 0.1; }
  }
`;

const ChatWrapper = () => {
  const {
    isAuthenticated,
    statusReceived,
    messages,
    isLoading,
    setIsLoading,
    wishlistItems,
    error,
    isReconnecting,
    reconnectCountdown,
    reconnectAttempts,
    MAX_RECONNECT_ATTEMPTS,
    handleManualReconnect,
    handleSend,
    handleSendOperatorOnly,
    handleResendLastMessage,
    handleWishlistUpdate,
    isHumanControlled,
    humanControlMessage,
    controller,
    mandatoryForm,
    automaticHumanControl,
    isConnected,
    accessToken,
    isChatVisible,
    toggleChat,
    notification,
    activeTrigger,
    openedByTrigger,
    setOpenedByTrigger,
    chatId,
    websocketRef,
  } = useWebSocketContext();

  // If for some reason openedByTrigger is undefined in the context, initialize it locally
  const [localOpenedByTrigger, setLocalOpenedByTrigger] = useState(false);
  
  // Use either the context value or local state as a fallback
  const effectiveOpenedByTrigger = openedByTrigger !== undefined ? openedByTrigger : localOpenedByTrigger;
  const effectiveSetOpenedByTrigger = setOpenedByTrigger || setLocalOpenedByTrigger;

  const [isChatWide, setChatWide] = useState(false);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [showReconnectionUI, setShowReconnectionUI] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isCarSearchActive, setIsCarSearchActive] = useState(false);
  const [isWelcomeScreenShowing, setIsWelcomeScreenShowing] = useState(true); // Start as true to hide search panel initially
  
  // Add state to track compact mode for mobile
  const [isCompactMode, setIsCompactMode] = useState(false);
  
  // Simplified form tracking
  const [showMandatoryForm, setShowMandatoryForm] = useState(false);
  
  // Push notification modal state (temporarily disabled)
  const [showPushModal, setShowPushModal] = useState(false);
  const language = useMemo(() => getBrowserLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  
  // Set up push notification modal callback
  // TEMPORARILY DISABLED - uncomment to re-enable push notifications
  // useEffect(() => {
  //   pushNotificationManager.setShowModalCallback(setShowPushModal);
  //   return () => {
  //     pushNotificationManager.setShowModalCallback(null);
  //   };
  // }, []);
  
  // Add MIA_ID to WhatsApp buttons when page loads
  useEffect(() => {
    const updateWhatsAppButtons = () => {
      // Get chat ID from localStorage or current context
      const storedChatId = localStorage.getItem("aitomotivelab_personalData_chat_id");
      const activeChatId = chatId || storedChatId;
      
      if (activeChatId && activeChatId !== "undefined" && activeChatId !== "null") {
        document.querySelectorAll('.btn-whatsapp').forEach(btn => {
          const href = btn.getAttribute('href');
          if (href && !href.includes('MIA_ID=')) {
            const url = new URL(href);
            const text = url.searchParams.get('text') || '';
            url.searchParams.set('text', `${text} MIA_ID=${activeChatId}`);
            btn.setAttribute('href', url.toString());
            
            // Add click event listener if not already added
            if (!btn.hasAttribute('data-mia-listener')) {
              btn.addEventListener('click', () => {
                // Send WebSocket message with navigation_stats structure
                const message = {
                  type: 'navigation_stats',
                  stats_id: `whatsapp_click_${Date.now()}`,
                  page: {
                    current_url: window.location.href,
                    title: document.title,
                    timestamp: new Date().toISOString(),
                  },
                  clicked: 'whatsapp_click',
                  time: new Date().toISOString()
                };
                
                if (websocketRef?.current?.readyState === WebSocket.OPEN) {
                  websocketRef.current.send(JSON.stringify(message));
                }
              });
              btn.setAttribute('data-mia-listener', 'true');
            }
          }
        });
      }
    };

    // Only update if chat_id is available, otherwise wait for WebSocket to establish it
    if (chatId) {
      updateWhatsAppButtons();
    }
  }, [chatId, websocketRef]);
  


  // We track the "active" search index => defaults to the LAST search result
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [lastSearchResultsLength, setLastSearchResultsLength] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // We'll create a ref to ChatBody so we can scroll to a particular message
  const chatBodyRef = useRef(null);

  // Add a ref to track the last known carousel content
  const lastCarouselContentRef = useRef(null);

  // Add ref to track previous message count
  const prevMessageCountRef = useRef(0);

  // Tracks previous value of isWelcomeScreenShowing to detect the transition to false
  const prevIsWelcomeScreenShowingRef = useRef(true);

  const toggleChatWidth = () => setChatWide(!isChatWide);

  const toggleSearchPanel = () => {
    if (!isMobile) {
      setSearchPanelOpen((open) => !open);
    }
  };
  
  // Add callback function to track car search state
  const handleCarSearchStateChange = (isOpen) => {
    setIsCarSearchActive(isOpen);
    if (isOpen) {
      // When car search opens, ensure the chat container doesn't interfere
      document.body.style.overflow = 'hidden';
      const chatContainer = document.querySelector('.chat-container');
      if (chatContainer) {
        chatContainer.classList.add('car-search-active');
      }
    } else {
      // When car search closes, restore normal state
      document.body.style.overflow = '';
      const chatContainer = document.querySelector('.chat-container');
      if (chatContainer) {
        chatContainer.classList.remove('car-search-active');
      }
    }
  };

  // Add callback function to track welcome screen state
  const handleWelcomeScreenStateChange = (isShowing) => {
    setIsWelcomeScreenShowing(isShowing);
    
    // Immediately close search panel when welcome screen is showing
    if (isShowing && searchPanelOpen) {
      setSearchPanelOpen(false);
    }
  };

  // Effect to ensure search panel is closed when welcome screen is showing
  useEffect(() => {
    if (isWelcomeScreenShowing && searchPanelOpen) {
      setSearchPanelOpen(false);
    }
  }, [isWelcomeScreenShowing, searchPanelOpen]);

  // Effect to handle body scroll based on chat visibility and mode
  useEffect(() => {
    const isReducedMobile = isMobile && isChatVisible && (isCompactMode || effectiveOpenedByTrigger);
    const isFullScreenMobile = isMobile && isChatVisible && !isCompactMode && !effectiveOpenedByTrigger;

    if (isFullScreenMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      // Allow scrolling if chat is hidden, on desktop, or reduced on mobile
      document.body.style.overflow = 'auto';
    }

    // Cleanup function
    return () => {
      // Ensure scroll is re-enabled when component unmounts or state changes
      document.body.style.overflow = 'auto';
    };
  }, [isMobile, isChatVisible, isCompactMode, effectiveOpenedByTrigger]);

  // Show the connection / reconnection overlay immediately whenever
  // the socket is *not* connected. This eliminates the brief "white flash"
  // that previously occurred while waiting 3 s before displaying the UI.
  useEffect(() => {
    setShowReconnectionUI(!isConnected);
  }, [isConnected]);

  useEffect(() => {
    if (isHumanControlled && isLoading) {
      setIsLoading(false);
    }
  }, [isHumanControlled, isLoading, setIsLoading]);

  useEffect(() => {
    // Close search panel when chat is closed
    if (!isChatVisible) {
      setSearchPanelOpen(false);
    }
  }, [isChatVisible]);

  // Simplified form logic - just basic logging for now
  useEffect(() => {
    // Form conditions changed
    
    // Simple form show/hide logic - we'll rebuild this properly after seeing the logs
    if (mandatoryForm && automaticHumanControl) {
      if (!showMandatoryForm) {
        // Showing mandatory form
        setShowMandatoryForm(true);
      }
    } else {
      if (showMandatoryForm) {
        // Hiding mandatory form
        setShowMandatoryForm(false);
      }
    }
  }, [mandatoryForm, automaticHumanControl, showMandatoryForm]);

  const handleToggleChat = (forceOpenedByTriggerValue) => {
    // Set a flag for animating to avoid flickering
    document.body.classList.add('chat-animating');
    
    if (!isChatVisible) {
      setSearchPanelOpen(false);
      
      // When manually toggling, ensure full screen on mobile (not compact)
      if (isMobile) {
        // Check if we have an explicit value for openedByTrigger
        if (forceOpenedByTriggerValue !== undefined) {
          effectiveSetOpenedByTrigger(forceOpenedByTriggerValue);
          // Set compact mode based on openedByTrigger value
          setIsCompactMode(forceOpenedByTriggerValue);
        } else {
          // Default behavior - full screen when clicked
          setIsCompactMode(false);
          if (effectiveOpenedByTrigger) {
            effectiveSetOpenedByTrigger(false);
          }
        }
      }
    } else if (isMobile) {
      // When closing on mobile, ensure we clean up state
      document.body.style.overflow = 'auto';
      // Force immediate cleanup of any DOM elements to prevent flicker
      const chatContainer = document.querySelector('.mia-ChatAndSearchContainer');
      if (chatContainer) {
        chatContainer.style.opacity = '0';
        chatContainer.style.visibility = 'hidden';
      }
    }
    
    toggleChat();
    
    // Remove animation flag after animation completes
    setTimeout(() => {
      document.body.classList.remove('chat-animating');
    }, 500);
  };

  /**
   * Partition messages for desktop:
   * "chatMessages" => normal text chat
   * "searchResults" => carousels from `car_stock_search`
   * For mobile, keep them all in the chat.
   */
  const { chatMessages, searchResults } = useMemo(
    () => partitionMessages(messages, isMobile),
    [messages, isMobile]
  );

  /**
   * Watch for changes in searchResults and only open/update panel
   * if there are actually new carousel items
   */
  useEffect(() => {
    if (!isMobile && searchResults.length > 0) {
      // Get the latest search result's carousel items
      const latestCarouselItems = searchResults[searchResults.length - 1]?.carouselItems;
      
      // Create a content signature for comparison
      const currentContent = latestCarouselItems ? JSON.stringify(latestCarouselItems) : null;
      
      // Only update if we have new carousel content
      if (currentContent && currentContent !== lastCarouselContentRef.current) {
        setSearchPanelOpen(true);
        // Only update currentSearchIndex if we have new content
        setCurrentSearchIndex(searchResults.length - 1);
        lastCarouselContentRef.current = currentContent;
      }
    }
  }, [searchResults, isMobile]);

  // Update handleNavigateSearch to be more strict about boundaries
  const handleNavigateSearch = (delta) => {
    setCurrentSearchIndex((prev) => {
      const newIndex = prev + delta;
      // Only allow navigation within valid search results
      if (newIndex < 0 || newIndex >= searchResults.length) {
        return prev; // Keep current index if out of bounds
      }
      return newIndex;
    });
  };

  /**
   * Whenever currentSearchIndex changes,
   * we can scroll the chat to the corresponding original message if we want
   */
  useEffect(() => {
    const target = searchResults[currentSearchIndex];
    if (target && chatBodyRef.current && typeof chatBodyRef.current.scrollToMessage === "function") {
      chatBodyRef.current.scrollToMessage(target.originalIndex);
    }
  }, [currentSearchIndex, searchResults]);

  // Watch for changes in searchResults
  useEffect(() => {
    if (!isMobile && searchResults.length > lastSearchResultsLength) {
      setSearchPanelOpen(true);
      setLastSearchResultsLength(searchResults.length);
    }
  }, [searchResults, isMobile, lastSearchResultsLength]);

  // When the welcome screen disappears and history was already loaded with carousel items,
  // re-open the search panel (history load fires before welcome screen hides, so the
  // guard effect closes it immediately — this reopens it at the right time)
  useEffect(() => {
    const wasShowing = prevIsWelcomeScreenShowingRef.current;
    prevIsWelcomeScreenShowingRef.current = isWelcomeScreenShowing;

    if (wasShowing && !isWelcomeScreenShowing && !isMobile && searchResults.length > 0) {
      setSearchPanelOpen(true);
      setCurrentSearchIndex(searchResults.length - 1);
    }
  }, [isWelcomeScreenShowing, isMobile, searchResults]);

  // Find the most recent predicted questions message
  const getLatestPredictedQuestions = useMemo(() => {
    // Find all messages with type "predicted_questions"
    const questionMessages = messages.filter(m => m.type === "predicted_questions");
    // Return the questions from the most recent one, or an empty array
    return questionMessages.length > 0 
      ? questionMessages[questionMessages.length - 1].questions || []
      : [];
  }, [messages]);

  const handleFormCompletion = () => {
    console.log('📝 FORM COMPLETED - need to implement new logic here');
    setShowMandatoryForm(false);
    localStorage.setItem("isSubmitted", "true");
  };

  // Add handler for toggling compact mode
  const toggleCompactMode = () => {
    setIsCompactMode(!isCompactMode);
  };

  // Handler to immediately remove transparency when chat is interacted with
  const handleChatInteraction = () => {
    const personalDataForm = document.querySelector('.personal-data-form, .personal-data-form-container');
    if (personalDataForm) {
      document.body.classList.add('personal-data-form-active');
      personalDataForm.addEventListener('submit', () => {
        document.body.classList.remove('personal-data-form-active');
      }, { once: true });
    }
  };

  // Cleanup body class when chat closes or component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove('personal-data-form-active');
    };
  }, []);

  // Update effect to handle openedByTrigger properly on mobile
  useEffect(() => {
    if (isChatVisible && isMobile && effectiveOpenedByTrigger) {
      // When opened by trigger on mobile, always use compact mode
      setIsCompactMode(true);
    }
  }, [isChatVisible, isMobile, effectiveOpenedByTrigger]);

  // Add effect to expand chat to full size when a new message arrives in reduced mode
  useEffect(() => {
    // Only proceed if chat is visible and in reduced mode
    const isReducedMobile = isMobile && isChatVisible && (isCompactMode || effectiveOpenedByTrigger);
    
    if (isReducedMobile && messages.length > 0) {
      // Get the latest message
      const latestMessage = messages[messages.length - 1];
      
      // Check if it's a bot/AI message (not from the user)
      if (latestMessage && !latestMessage.isUser) {
        // If we have a new message from the AI
        if (messages.length > prevMessageCountRef.current) {
          // Expand the chat to full size
          setIsCompactMode(false);
          if (effectiveOpenedByTrigger) {
            effectiveSetOpenedByTrigger(false);
          }
          
          // Give the UI time to update before scrolling to the new message
          setTimeout(() => {
            // Scroll to the latest message
            const chatMessages = document.querySelector('.chat-messages');
            const lastMessage = chatMessages?.querySelector('.mia-aitomotivelab-message-container:last-child');
            
            if (lastMessage) {
              lastMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
              
              // Add a brief highlight effect to the new message
              lastMessage.classList.add('new-message-highlight');
              setTimeout(() => {
                lastMessage.classList.remove('new-message-highlight');
              }, 2000);
            }
          }, 300);
        }
      }
    }
    
    // Always update the counter at the end
    prevMessageCountRef.current = messages.length;
  }, [messages, isMobile, isChatVisible, isCompactMode, effectiveOpenedByTrigger, effectiveSetOpenedByTrigger]);

  return (
    <>
      <AnimatePresence 
        mode="wait" 
        initial={false}
        onExitComplete={() => {
          // Make sure body overflow is reset when chat exits
          if (isMobile) {
            document.body.style.overflow = 'auto';
            // Cleanup any lingering elements after animation completes
            const ghostContainers = document.querySelectorAll('.mia-ChatAndSearchContainer');
            ghostContainers.forEach(el => {
              if (el.parentNode) {
                el.style.display = 'none';
              }
            });
          }
        }}
      >
        {isChatVisible && (
          <MiaChatAndSearchContainer
            $isMobile={isMobile}
            $openedByTrigger={effectiveOpenedByTrigger}
            $isCompactMode={isCompactMode}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ 
              opacity: 0, 
              y: isMobile ? 50 : 50,
              pointerEvents: 'none',
              position: 'fixed',
              transition: {
                duration: isMobile ? 0.15 : 0.2, // Faster exit for mobile
                ease: "easeOut"
              }
            }}
            transition={{ 
              type: "tween",
              duration: isMobile ? 0.2 : 0.3,
              ease: "easeInOut" 
            }}
            className={`mia-ChatAndSearchContainer ${isCarSearchActive ? 'mia-car-search-active' : ''} ${isCompactMode ? 'mia-isCompactMode' : ''} ${effectiveOpenedByTrigger ? 'mia-openedByTrigger' : ''}`}
            onPointerDown={handleChatInteraction}
          >
            <ChatContainer 
              $isMobile={isMobile}
              className={isCarSearchActive ? 'car-search-active' : ''}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#fff",
                  borderRadius: isMobile ? 0 : "20px",
                  boxShadow: isMobile ? "none" : "0 8px 24px rgba(0, 0, 0, 0.08)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  position: "relative",
                  border: isMobile ? "none" : "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.1)",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      zIndex: 1001,
                      backgroundColor: theme.palette.v2.chatBackground,
                      userSelect: "none",
                      cursor: "default",
                      ...(isMobile && {
                        position: 'sticky',
                        top: 0,
                      })
                    }}
                  >
                    {/* Remove the ChatHeader component */}
                  </Box>

                  <Box
                    sx={{
                      position: "relative",
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      overflow: "hidden",
                      paddingTop: "0",
                      marginTop: 0,
                    }}
                  >
                    <CustomScrollbar>
                      <Fade in={isConnected} timeout={500}>
                        <Box
                          style={{
                            display: isConnected ? "block" : "none",
                            ...(isMobile && {
                              touchAction: 'pan-y',
                              WebkitOverflowScrolling: 'touch',
                            }),
                          }}
                        >
                          <ChatBody
                            ref={chatBodyRef}
                            messages={chatMessages.filter(
                              (m) => !m.type || m.type !== "predicted_questions"
                            )}
                            isLoading={isLoading}
                            onSendMessage={(message, replyInfo) => {
                              // When sending message, clear compact mode and openedByTrigger
                              if (isCompactMode) setIsCompactMode(false);
                              if (effectiveOpenedByTrigger) effectiveSetOpenedByTrigger(false);
                              handleSend(message, null, null, null, replyInfo);
                            }}
                            onSendOperatorOnly={handleSendOperatorOnly}
                            onSendFile={(file, message) => {
                              // When sending file, clear compact mode and openedByTrigger 
                              if (isCompactMode) setIsCompactMode(false);
                              if (effectiveOpenedByTrigger) effectiveSetOpenedByTrigger(false);
                              handleSend(message, file);
                            }}
                            onSendPersonalData={(personaldata) => {
                              // When sending personal data, clear compact mode and openedByTrigger
                              if (isCompactMode) setIsCompactMode(false);
                              if (effectiveOpenedByTrigger) effectiveSetOpenedByTrigger(false);
                              
                              // Check if we're in offline workflow mode
                              const isOfflineWorkflow = mandatoryForm && automaticHumanControl;
                              
                              if (isOfflineWorkflow) {
                                // For offline workflow, only mark form as completed
                                // Don't send anything here - let handleOfflineFormCompletion handle everything
                                console.log('CHATWRAPPER: Offline workflow detected - deferring send to completion handler');
                                handleFormCompletion();
                              } else {
                                // For normal workflow, mark form as completed and send data
                                handleFormCompletion();
                                
                                handleSend(
                                  "Dati personali salvati",
                                  null,
                                  null,
                                  personaldata
                                );
                              }
                            }}
                            isConnected={isConnected}
                            isAuthenticated={isAuthenticated}
                            statusReceived={statusReceived}
                            isHumanControlled={isHumanControlled}
                            humanControlMessage={humanControlMessage}
                            controller={controller}
                            mandatoryForm={mandatoryForm}
                            automaticHumanControl={automaticHumanControl}
                            showMandatoryForm={showMandatoryForm}
                            onFormCompletion={handleFormCompletion}
                            setIsLoading={setIsLoading}
                            accessToken={accessToken}
                            toggleChat={toggleChat}
                            toggleChatWidth={toggleChatWidth}
                            isChatWide={isChatWide}
                            toggleSearchPanel={toggleSearchPanel}
                            predictedQuestions={getLatestPredictedQuestions}
                            onQuestionClick={(question) => {
                              setInputValue(question);
                              // When clicking a question, clear compact mode and openedByTrigger
                              if (isCompactMode) setIsCompactMode(false);
                              if (effectiveOpenedByTrigger) effectiveSetOpenedByTrigger(false);
                              handleSend(question);
                            }}
                            isMobile={isMobile}
                            inputValue={inputValue}
                            setInputValue={setInputValue}
                            onCarSearchStateChange={handleCarSearchStateChange}
                            onWelcomeScreenStateChange={handleWelcomeScreenStateChange}
                            openedByTrigger={effectiveOpenedByTrigger}
                            setOpenedByTrigger={effectiveSetOpenedByTrigger}
                            isCompactMode={isCompactMode}
                            toggleCompactMode={toggleCompactMode}
                          />
                        </Box>
                      </Fade>

                      <Fade in={!isConnected && showReconnectionUI} timeout={300}>
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(5px)",
                          }}
                        >
                          <Grow in={!isConnected && showReconnectionUI} timeout={500}>
                            <Typography
                              variant="h5"
                              sx={{
                                mb: 2,
                                fontWeight: "bold",
                                color: theme.palette.primary.main,
                              }}
                            >
                              {isReconnecting
                                ? t("connectionStatusReconnecting")
                                : (reconnectAttempts === 0
                                    ? t("connectionStatusConnecting")
                                    : t("connectionStatusLost"))}
                            </Typography>
                          </Grow>
                          <ConnectionStatus
                            isConnected={isConnected}
                            isReconnecting={isReconnecting}
                            reconnectCountdown={reconnectCountdown}
                            reconnectAttempts={reconnectAttempts}
                            MAX_RECONNECT_ATTEMPTS={MAX_RECONNECT_ATTEMPTS}
                            onManualReconnect={handleManualReconnect}
                          />
                        </Box>
                      </Fade>
                    </CustomScrollbar>
                  </Box>

                  <Box
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      zIndex: 10,
                      backgroundColor: theme.palette.v2.chatBackground,
                      userSelect: "none",
                      cursor: "default",
                    }}
                  >
                    <Fade in={isConnected} timeout={500}>
                      <div style={{ display: isConnected ? "block" : "none" }}>
                        <div></div>
                      </div>
                    </Fade>
                  </Box>
                </Box>
                
                {/* Push Notification Modal */}
                <PushNotificationModal
                  isOpen={showPushModal}
                  onClose={() => setShowPushModal(false)}
                  onSuccess={() => console.log('Push notifications enabled!')}
                  colors={{ primary: theme.palette.primary.main }}
                />
              </Box>
            </ChatContainer>

            <AnimatePresence>
              {searchPanelOpen && !isMobile && !isWelcomeScreenShowing && (
                <SearchPanelContainer
                  initial={{ 
                    opacity: 0, 
                    x: 60,
                    scale: 0.95,
                    rotateY: -10
                  }}
                  animate={{ 
                    opacity: 1, 
                    x: 0,
                    scale: 1,
                    rotateY: 0,
                    transition: { 
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                      duration: 0.6,
                      ease: "easeOut"
                    }
                  }}
                  exit={{ 
                    opacity: 0, 
                    x: 60,
                    scale: 0.95,
                    rotateY: -10,
                    transition: { 
                      duration: 0.4,
                      ease: "easeIn"
                    }
                  }}
                >
                  <SearchPanel
                    searchMessages={
                      searchResults.length > 0
                        ? [searchResults[currentSearchIndex]]
                        : []
                    }
                    onNavigateSearch={handleNavigateSearch}
                    currentIndex={currentSearchIndex}
                    totalCount={searchResults.length}
                    onClose={() => setSearchPanelOpen(false)}
                    isLoading={isLoading}
                    isHumanControlled={isHumanControlled}
                  />
                </SearchPanelContainer>
              )}
            </AnimatePresence>

            {/* Replace the floating search button with the semicircle button */}
            <AnimatePresence>
              {!isMobile && searchResults.length > 0 && !searchPanelOpen && !isWelcomeScreenShowing && (
                <Tooltip title={t("openSearchPanel")} placement="left">
                  <SemicircleButton
                    onClick={() => setSearchPanelOpen(true)}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    whileHover={{ x: -5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ResultBadge
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      {searchResults.length}
                    </ResultBadge>
                    <IconWrapper>
                      <KeyboardDoubleArrowLeftIcon 
                        style={{ 
                          width: 32, 
                          height: 32
                        }} 
                      />
                    </IconWrapper>
                  </SemicircleButton>
                </Tooltip>
              )}
            </AnimatePresence>
          </MiaChatAndSearchContainer>
        )}
      </AnimatePresence>
      {!isChatVisible && (
        <NewToggleChat 
          isChatVisible={isChatVisible} 
          toggleChat={handleToggleChat}
          onSendMessage={(message) => handleSend(message)}
          onPopulateInput={setInputValue}
          notification={notification}
          chatBodyRef={chatBodyRef}
        />
      )}
      
      {/* Desktop close button when chat is visible */}
      {isChatVisible && !isMobile && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          onClick={handleToggleChat}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            background: '#fff',
            border: '2px solid #3B82F6',
            color: '#3B82F6',
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.25)',
            transition: 'all 0.3s ease',
            zIndex: 1001,
          }}
          whileHover={{
            background: '#3B82F6',
            color: '#fff',
            scale: 1.05,
            boxShadow: '0 12px 32px rgba(59, 130, 246, 0.4)',
          }}
          whileTap={{ scale: 0.95 }}
          title={t("closeChat")}
          aria-label={t("closeChat")}
        >
          <X size={32} strokeWidth={2} />
        </motion.button>
      )}
    </>
  );
};

export default ChatWrapper;
